import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";

const MN = ["", "Січ", "Лют", "Бер", "Кві", "Тра", "Чер", "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];
const MOMENTUM_HELP = "Моментум — короткостроковий напрямок попиту за останні ~3 місяці (у порівнянні з попередніми 3), очищений від сезонності. ↑ росте (≥ +8%), → стабільно (±8%), ↓ падає (≤ −8%). На відміну від YoY (рік до року) показує, що з попитом відбувається ПРЯМО ЗАРАЗ.";

// Міні-графік помісячної динаміки.
function Spark({ monthly, color }) {
  if (!monthly || !monthly.length) return <span style={{ color: "#9ca3af" }}>—</span>;
  // Починаємо графік із січня поточного року (рік беремо з останньої точки даних) до останнього місяця.
  const lastYm = String(monthly[monthly.length - 1].month || "");
  const yr = lastYm.slice(0, 4);
  const jan = yr ? monthly.filter((m) => String(m.month) >= yr + "-01") : monthly;
  const series = jan.length ? jan : monthly;
  const vals = series.map((m) => m.value);
  const mx = Math.max(...vals, 1), n = vals.length;
  const W = 120, H = 24;
  const pts = vals.map((v, i) => `${(i / Math.max(1, n - 1)) * W},${H - (v / mx) * H}`).join(" ");
  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  );
}

// Стрілка короткострокового моментуму.
function Momentum({ m, pct }) {
  const map = {
    up: { a: "↑", c: "#16a34a", t: "росте" },
    down: { a: "↓", c: "#dc2626", t: "падає" },
    flat: { a: "→", c: "#6b7280", t: "стабільно" },
  };
  const v = map[m] || map.flat;
  return (
    <span title={`Моментум: ${v.t}${pct != null ? ` (${pct > 0 ? "+" : ""}${pct}% ост.3 міс)` : ""}`} style={{ color: v.c, fontWeight: 800, fontSize: 15 }}>
      {v.a}{pct != null && <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 2 }}>{pct > 0 ? "+" : ""}{pct}%</span>}
    </span>
  );
}

export default function TrendsView() {
  const { activeId, apiFetch, t } = useApp();
  const [geo, setGeo] = useState("UA");
  const [pagesData, setPagesData] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [filter, setFilter] = useState("");
  const [onlyLoaded, setOnlyLoaded] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(null);
  const [expanded, setExpanded] = useState({}); // url -> bool
  const [tab, setTab] = useState("pages"); // pages | forecast
  const [msg, setMsg] = useState("");
  // Ad-hoc: довільні ключі поза ядром
  const [adhoc, setAdhoc] = useState("");
  const [adhocOpen, setAdhocOpen] = useState(false);
  // Повна перевірка всіх ключів ядра (фонова задача) + прогрес
  const [checking, setChecking] = useState(false);
  const [checkProg, setCheckProg] = useState(null); // { total, done, ok, err, current }
  const pollRef = useRef(null);
  // Прогноз: поріг піку у % над середнім попитом (індекс сезонності 1 + pct/100)
  const [peakPct, setPeakPct] = useState(15);
  const [posMap, setPosMap] = useState({}); // keyword(lower) → позиція з останнього знімку (бейдж #N)

  const loadPages = useCallback(async () => {
    if (!activeId) return;
    try {
      const d = await apiFetch(`/api/projects/${activeId}/trends/pages?geo=${geo}`).then((r) => r.json());
      setPagesData(d || null);
    } catch (e) { /* ignore */ }
  }, [apiFetch, activeId, geo]);

  const loadForecast = useCallback(async () => {
    if (!activeId) return;
    try {
      const d = await apiFetch(`/api/projects/${activeId}/trends/forecast?geo=${geo}`).then((r) => r.json());
      setForecast(d || null);
    } catch (e) { /* ignore */ }
  }, [apiFetch, activeId, geo]);

  useEffect(() => { loadPages(); loadForecast(); }, [loadPages, loadForecast]);

  // Позиції ключів з останнього знімку — для бейджа #N біля запиту.
  useEffect(() => {
    if (!activeId) { setPosMap({}); return; }
    apiFetch(`/api/projects/${activeId}/positions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const snaps = (d && d.snapshots) || [];
        const last = snaps[snaps.length - 1];
        const m = {};
        (last?.keywords || []).forEach((k) => { if (k && k.keyword) m[String(k.keyword).toLowerCase().trim()] = k.position; });
        setPosMap(m);
      })
      .catch(() => {});
  }, [activeId, apiFetch]);

  // Завантажити тренди для конкретної сторінки (її ключі з ядра).
  const loadPageTrends = async (page) => {
    const kws = (page.keywords || []).map((k) => k.keyword);
    if (!kws.length) return;
    setLoadingUrl(page.url); setMsg("");
    try {
      await apiFetch(`/api/projects/${activeId}/trends/keywords`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keywords: kws, geo }),
      }).then((r) => r.json());
      setExpanded((e) => ({ ...e, [page.url]: true }));
      await Promise.all([loadPages(), loadForecast()]);
    } catch (e) { setMsg("Помилка: " + e.message); }
    setLoadingUrl(null);
  };

  const loadAdhoc = async () => {
    const kws = adhoc.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!kws.length) return;
    setLoadingUrl("__adhoc__"); setMsg("");
    try {
      const d = await apiFetch(`/api/projects/${activeId}/trends/keywords`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keywords: kws, geo }),
      }).then((r) => r.json());
      setMsg(`Завантажено ${(d.keywords || []).length} ключів`);
      setAdhoc("");
      await Promise.all([loadPages(), loadForecast()]);
    } catch (e) { setMsg("Помилка: " + e.message); }
    setLoadingUrl(null);
  };

  // Опитування статусу фонової перевірки
  const pollStatus = useCallback(() => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    const tick = async () => {
      try {
        const s = await apiFetch(`/api/projects/${activeId}/trends/check-all/status?geo=${geo}`).then((r) => r.json());
        if (s && s.running) {
          setChecking(true);
          setCheckProg(s.running.progress || {});
          pollRef.current = setTimeout(tick, 2500);
        } else {
          setChecking(false);
          setMsg("Перевірку завершено ✓");
          await Promise.all([loadPages(), loadForecast()]);
        }
      } catch { pollRef.current = setTimeout(tick, 4000); }
    };
    pollRef.current = setTimeout(tick, 1500);
  }, [apiFetch, activeId, geo, loadPages, loadForecast]);

  // Запустити повну перевірку всіх ключів ядра (з підтвердженням вартості)
  const checkAll = async () => {
    try {
      const est = await apiFetch(`/api/projects/${activeId}/trends/check-all`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ geo }),
      }).then((r) => r.json());
      if (!est || est.error) { setMsg("Помилка: " + (est?.error || "")); return; }
      if (!est.toFetch) { setMsg("Усі ключі ядра вже перевірені ✓"); return; }
      const mins = Math.max(1, Math.round((est.toFetch / 4) * 1.6 / 60));
      const ok = window.confirm(
        `Перевірити тренди по ВСІХ запитах семантичного ядра?\n\n` +
        `Завантажити: ${est.toFetch} ключів (з ${est.total}; решта вже в кеші).\n` +
        `Орієнтовна вартість: $${est.estCost} (DataForSEO).\n` +
        `Час: ~${mins} хв у фоні. Можна закрити вкладку — продовжиться на сервері.`
      );
      if (!ok) return;
      const st = await apiFetch(`/api/projects/${activeId}/trends/check-all`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ geo, start: true }),
      }).then((r) => r.json());
      if (!st || !st.started) { setMsg(st?.toFetch === 0 ? "Усі ключі вже в кеші ✓" : "Не вдалося запустити"); return; }
      setChecking(true); setCheckProg({ total: st.toFetch, done: 0, ok: 0, err: 0 });
      pollStatus();
    } catch (e) { setMsg("Помилка: " + e.message); }
  };

  // Відновити опитування, якщо перевірка вже йде (повернулись у вкладку)
  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/trends/check-all/status?geo=${geo}`).then((r) => r.json()).then((s) => {
      if (s && s.running) { setChecking(true); setCheckProg(s.running.progress || {}); pollStatus(); }
    }).catch(() => {});
    return () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };
  }, [activeId, geo, apiFetch, pollStatus]);

  const box = { background: t?.card || "#fff", border: `1px solid ${t?.border || "#e5e7eb"}`, borderRadius: 10, padding: 16, marginBottom: 16 };
  const yoyColor = (v) => v == null ? "#9ca3af" : v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#6b7280";
  const posColor = (p) => p == null ? "#9ca3af" : p <= 3 ? "#16a34a" : p <= 10 ? "#2563eb" : p <= 20 ? "#f59e0b" : "#6b7280";
  const yoyLabel = (v) => v == null ? "—" : (v > 0 ? "+" : "") + v + "%";
  const shortUrl = (u) => { try { const x = new URL(u); return x.pathname + x.search; } catch { return u; } };

  if (!activeId) {
    return <div style={{ maxWidth: 700, margin: "40px auto", color: t?.textMuted, textAlign: "center" }}>
      <h2 style={{ color: t?.text }}>📈 Google Trends</h2>
      <p>Оберіть проєкт, щоб переглянути тренди.</p>
    </div>;
  }

  const KwRow = ({ r }) => (
    <tr style={{ borderTop: `1px solid ${t?.border}` }}>
      <td style={{ padding: "6px 8px" }}>{r.keyword}{(() => { const p = posMap[String(r.keyword).toLowerCase().trim()]; return p ? <span title={"Позиція: " + p} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 5, color: "#fff", background: posColor(p) }}>#{p}</span> : null; })()}<span style={{ color: t?.textMuted, fontSize: 10, marginLeft: 6 }}>{r.frequency ? r.frequency.toLocaleString("uk-UA") : ""}</span></td>
      <td style={{ padding: "6px 8px" }}>{r.trend ? <Momentum m={r.trend.momentum} pct={r.trend.momentumPct} /> : <span style={{ color: "#9ca3af" }}>—</span>}</td>
      <td style={{ padding: "6px 8px", fontWeight: 700, color: yoyColor(r.trend?.yoy) }}>{r.trend ? yoyLabel(r.trend.yoy) : "—"}</td>
      <td style={{ padding: "6px 8px" }}>{r.trend?.peakMonth ? MN[r.trend.peakMonth] : "—"}</td>
      <td style={{ padding: "6px 8px" }}>{r.trend ? <Spark monthly={r.trend.monthly} color="#6366f1" /> : <span style={{ color: "#9ca3af" }}>—</span>}</td>
    </tr>
  );

  const pages = pagesData?.pages || [];
  const flt = filter.trim().toLowerCase();
  const visible = pages.filter((p) => {
    if (onlyLoaded && !p.loadedCount) return false;
    if (!flt) return true;
    return (p.hfKeyword || "").toLowerCase().includes(flt) || (p.url || "").toLowerCase().includes(flt)
      || (p.keywords || []).some((k) => k.keyword.toLowerCase().includes(flt));
  });

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto", color: t?.text }}>
      <h2 style={{ fontSize: 22, margin: "4px 0 2px" }}>📈 Google Trends · сезонність, моментум, YoY</h2>
      <div style={{ color: t?.textMuted, fontSize: 13, marginBottom: 16 }}>Реальні дані Google Trends (через DataForSEO). Розбивка по сторінках — із семантичного ядра. Стрілка — короткостроковий напрямок (ост. 3 міс), YoY — рік до року, пік — місяць найвищого попиту.</div>

      {/* Таби */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
        {[["pages", "🗂 За сторінками"], ["forecast", "🗓 Прогноз на рік"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ background: tab === k ? "linear-gradient(135deg,#6366f1,#a855f7)" : "none", color: tab === k ? "#fff" : t?.textMuted, border: `1px solid ${tab === k ? "#6366f1" : t?.border}`, borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
        ))}
        <select value={geo} onChange={(e) => setGeo(e.target.value)} style={{ marginLeft: "auto", background: t?.bg, color: t?.text, border: `1px solid ${t?.border}`, borderRadius: 8, padding: "6px 10px", fontFamily: "inherit" }}>
          <option value="UA">Україна</option><option value="US">США</option><option value="PL">Польща</option>
        </select>
        {msg && <span style={{ fontSize: 12, color: t?.textMuted }}>{msg}</span>}
      </div>

      {/* ── Таб: За сторінками ── */}
      {tab === "pages" && (
        <>
          <div style={{ ...box, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Пошук сторінки або ключа…"
              style={{ flex: "1 1 200px", background: t?.bg || "#fafafa", color: t?.text, border: `1px solid ${t?.border}`, borderRadius: 8, padding: "7px 10px", fontFamily: "inherit", fontSize: 13 }} />
            <label style={{ fontSize: 12, color: t?.textMuted, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={onlyLoaded} onChange={(e) => setOnlyLoaded(e.target.checked)} /> лише з трендами
            </label>
            <span style={{ fontSize: 12, color: t?.textMuted }}>
              {pagesData ? `${pagesData.pageCount} сторінок · ${pagesData.pagesWithTrends} з трендами` : "завантаження…"}
            </span>
            <button onClick={checkAll} disabled={checking} title="Завантажити тренди Google по ВСІХ запитах семантичного ядра (через DataForSEO)"
              style={{ marginLeft: "auto", background: checking ? (t?.bg || "#f3f4f6") : "linear-gradient(135deg,#6366f1,#a855f7)", color: checking ? t?.textMuted : "#fff", border: `1px solid ${checking ? t?.border : "#6366f1"}`, borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: checking ? "wait" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
              {checking ? "Перевірка триває…" : "⚡ Перевірити тренди по всіх запитах"}
            </button>
          </div>

          {checking && checkProg && (
            <div style={box}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: t?.textMuted, marginBottom: 6 }}>
                <span>Перевірка трендів: {checkProg.done || 0} / {checkProg.total || 0}{checkProg.current ? ` · ${checkProg.current}` : ""}</span>
                <span>✓ {checkProg.ok || 0}{checkProg.err ? ` · ⚠ ${checkProg.err}` : ""}</span>
              </div>
              <div style={{ height: 8, borderRadius: 6, background: t?.border || "#e5e7eb", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${checkProg.total ? Math.round((checkProg.done / checkProg.total) * 100) : 0}%`, background: "linear-gradient(90deg,#6366f1,#a855f7)", transition: "width .3s" }} />
              </div>
              <div style={{ fontSize: 11, color: t?.textMuted, marginTop: 6 }}>Працює у фоні на сервері — можна піти в інші модулі, прогрес збережеться.</div>
            </div>
          )}

          {visible.map((p, i) => {
            const open = expanded[p.url] ?? (p.loadedCount > 0);
            const isLoading = loadingUrl === p.url;
            return (
              <div key={i} style={box}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0, cursor: "pointer" }} onClick={() => setExpanded((e) => ({ ...e, [p.url]: !open }))}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{open ? "▾ " : "▸ "}{p.hfKeyword || shortUrl(p.url)}</div>
                    <a href={p.url} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()} style={{ fontSize: 11, color: "#6366f1", textDecoration: "none", wordBreak: "break-all" }}>{shortUrl(p.url)}</a>
                  </div>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    {p.agg ? (
                      <>
                        <span style={{ fontSize: 11, color: t?.textMuted }}>Тренд:</span>
                        <Momentum m={p.agg.momentum} pct={p.agg.momentumPct} />
                        <span style={{ fontSize: 11, color: t?.textMuted }}>YoY ∅</span>
                        <span style={{ fontWeight: 700, color: yoyColor(p.agg.yoy) }}>{yoyLabel(p.agg.yoy)}</span>
                      </>
                    ) : null}
                    <span style={{ fontSize: 11, color: t?.textMuted }}>{p.loadedCount}/{p.keywords.length} кл.{p.allCount > p.keywords.length ? ` (з ${p.allCount})` : ""}</span>
                    <button onClick={() => loadPageTrends(p)} disabled={isLoading} style={{ background: p.loadedCount ? "none" : "linear-gradient(135deg,#6366f1,#a855f7)", color: p.loadedCount ? t?.textMuted : "#fff", border: `1px solid ${p.loadedCount ? t?.border : "#6366f1"}`, borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: isLoading ? "wait" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                      {isLoading ? "Завантаження…" : p.loadedCount ? "↻ Оновити" : `Завантажити тренди (${p.keywords.length})`}
                    </button>
                  </span>
                </div>
                {open && (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 10 }}>
                    <thead><tr style={{ textAlign: "left", color: t?.textMuted, fontSize: 10, textTransform: "uppercase" }}>
                      <th style={{ padding: "5px 8px" }}>Запит</th>
                      <th style={{ padding: "5px 8px" }}><span title={MOMENTUM_HELP} style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}>Моментум ⓘ</span></th>
                      <th style={{ padding: "5px 8px" }}><span title="YoY — зміна попиту рік до року (сума останніх 12 міс vs попередніх 12). Довгостроковий тренд." style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}>YoY ⓘ</span></th>
                      <th style={{ padding: "5px 8px" }}>Пік</th><th style={{ padding: "5px 8px" }}>Динаміка</th>
                    </tr></thead>
                    <tbody>{p.keywords.map((r, j) => <KwRow key={j} r={r} />)}</tbody>
                  </table>
                )}
              </div>
            );
          })}
          {pagesData && visible.length === 0 && (
            <div style={{ ...box, textAlign: "center", color: t?.textMuted }}>Нічого не знайдено за фільтром.</div>
          )}

          {/* Ad-hoc ключі поза ядром */}
          <div style={box}>
            <div onClick={() => setAdhocOpen((v) => !v)} style={{ fontWeight: 700, cursor: "pointer", fontSize: 13 }}>{adhocOpen ? "▾" : "▸"} Довільні ключі (поза ядром)</div>
            {adhocOpen && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: t?.textMuted, fontSize: 12, marginBottom: 6 }}>Сезонні/тестові ключі, яких немає в семантичному ядрі. Потраплять у «Прогноз на рік» та в розбивку (якщо збігаються з ключами сторінки).</div>
                <textarea value={adhoc} onChange={(e) => setAdhoc(e.target.value)} rows={3} placeholder="По одному в рядку"
                  style={{ width: "100%", background: t?.bg || "#fafafa", color: t?.text, border: `1px solid ${t?.border}`, borderRadius: 8, padding: 10, fontFamily: "inherit", fontSize: 13, resize: "vertical", boxSizing: "border-box" }} />
                <button onClick={loadAdhoc} disabled={loadingUrl === "__adhoc__"} style={{ marginTop: 8, background: "linear-gradient(135deg,#6366f1,#a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontWeight: 700, cursor: loadingUrl === "__adhoc__" ? "wait" : "pointer", fontFamily: "inherit" }}>{loadingUrl === "__adhoc__" ? "Завантаження…" : "Завантажити"}</button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Таб: Прогноз на рік ── */}
      {tab === "forecast" && forecast && (() => {
        const thr = 1 + peakPct / 100; // поріг піку: індекс сезонності
        const peaksOf = (m) => (m.keywords || []).filter((k) => k.index >= thr).sort((a, b) => b.index - a.index);
        const maxPeakNow = Math.max(1, ...(forecast.months || []).map((m) => peaksOf(m).length));
        return (
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>🗓 Сезонний прогноз на 12 місяців вперед</div>
          <div style={{ color: t?.textMuted, fontSize: 12, marginBottom: 12 }}>Висота стовпця = к-сть ключів у піку цього місяця. У піку = попит на ключ ≥ середнього +{peakPct}%. Побудовано з усіх завантажених трендів.</div>
          {(!forecast.months || !forecast.keywordCount) ? (
            <div style={{ color: t?.textMuted }}>Немає завантажених трендів. Завантажте тренди у вкладці «За сторінками» або натисніть «⚡ Перевірити тренди по всіх запитах».</div>
          ) : (
            <>
              {/* Повзунок порога піку */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: t?.textMuted, whiteSpace: "nowrap" }}>Поріг піку: +{peakPct}% до середнього</span>
                <input type="range" min={0} max={100} step={5} value={peakPct} onChange={(e) => setPeakPct(Number(e.target.value))}
                  style={{ flex: "1 1 220px", accentColor: "#6366f1", cursor: "pointer" }} />
                <span style={{ fontSize: 11, color: t?.textMuted, whiteSpace: "nowrap" }}>менше ключів ← → більше</span>
              </div>

              <div style={{ display: "flex", gap: 6, alignItems: "flex-end", minHeight: 150 }}>
                {forecast.months.map((m, i) => {
                  const cnt = peaksOf(m).length;
                  const h = Math.max(6, Math.round((cnt / maxPeakNow) * 120));
                  const hot = cnt > 0;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: hot ? "#a855f7" : t?.textMuted }}>{cnt || ""}</div>
                      <div title={`${m.fullLabel} ${m.year} · у піку: ${cnt} ключів`}
                        style={{ width: "100%", maxWidth: 44, height: h, borderRadius: "6px 6px 0 0", background: hot ? "linear-gradient(180deg,#a855f7,#6366f1)" : (t?.border || "#e5e7eb"), transition: "height .2s", border: m.isCurrent ? "2px solid #16a34a" : "none" }} />
                      <div style={{ fontSize: 10, fontWeight: m.isCurrent ? 800 : 500, color: m.isCurrent ? "#16a34a" : t?.text }}>{m.label}</div>
                      <div style={{ fontSize: 9, color: t?.textMuted }}>{String(m.year).slice(2)}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 10 }}>
                {forecast.months.map((m, i) => {
                  const peaks = peaksOf(m);
                  if (!peaks.length) return null;
                  return (
                    <div key={i} style={{ border: `1px solid ${t?.border}`, borderRadius: 8, padding: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", color: "#a855f7", marginBottom: 6 }}>{m.fullLabel} {m.year}{m.isCurrent && " · зараз"} <span style={{ color: t?.textMuted, fontWeight: 400 }}>· {peaks.length}</span></div>
                      <div style={{ maxHeight: 280, overflowY: "auto" }}>
                        {peaks.map((k, j) => (
                          <div key={j} style={{ fontSize: 12, marginBottom: 4, display: "flex", justifyContent: "space-between", gap: 6 }}>
                            <span title={k.url || ""}>{k.keyword}{k.isPeak && " ⭐"}{k.frequency ? <span style={{ color: t?.textMuted, fontSize: 10, marginLeft: 5 }}>{k.frequency.toLocaleString("uk-UA")}/міс</span> : null}</span>
                            <span style={{ color: t?.textMuted, fontSize: 10, whiteSpace: "nowrap" }}>×{k.index}{k.yoy != null && <span style={{ color: yoyColor(k.yoy), marginLeft: 4 }}>{yoyLabel(k.yoy)}</span>}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        );
      })()}
    </div>
  );
}
